/**
 * dcent-x-control-center.js — X 기기 컨트롤센터 화면의 밝기 4단계 순환 연출.
 *
 * 원본: `20260827 homepage_전달용/src/components/XControlCenterDevice.tsx` 의 useEffect 2개.
 * 마크업은 `snippets/dcent-x-device-control-center.liquid`, 외형은 `dcent-x-devices.css`.
 *
 * 하는 일은 하나다 — 루트에 `data-level`(1~4)을 2초 간격으로 돌린다. **화면에 나타나는 변화는
 * 전부 CSS 가 그 속성을 보고 만든다**(칸 점등·액정 밝기·블룸·뒤 글로우). JS 가 style 을 직접
 * 만지지 않는 이유는, 연출값이 디자인 조율의 결과라 CSS 한 곳에 모여 있어야 나중에 고칠 때
 * 여기저기 흩어진 숫자를 쫓지 않아도 되기 때문이다.
 *
 * 안전 규약 (다른 dcent-* 연출 스크립트와 동일)
 *   - **보일 때만 재생** — IntersectionObserver. 화면 밖에서 타이머가 돌지 않는다.
 *   - `prefers-reduced-motion: reduce` 면 **정지**하고 기본 단계(2)로 둔다. 원본과 같은 판단이다.
 *   - 대상이 없으면 조용히 종료 — 이 스니펫이 없는 페이지에서 스크립트만 로드돼도 죽지 않는다.
 *   - 멱등 설치 — 같은 페이지에 두 번 실려도 타이머가 겹치지 않는다.
 *
 * 양 샵 byte-identical — assets/dcent-* 는 parity 게이트 분모다.
 */

if (!window.__dcentXControlCenter) {
  window.__dcentXControlCenter = true;

  /** 한 단계를 머무는 시간(ms). 원본이 1.1s → 2s 로 늦춘 값 — "속도가 너무 빠르다". */
  const HOLD = 2000;
  const MIN_LEVEL = 1;
  const MAX_LEVEL = 4;
  /** 정지 상태에서 보여줄 단계 — 피그마 기본값. */
  const STILL_LEVEL = 2;

  const init = () => {
    const roots = document.querySelectorAll("[data-x-cc]");
    if (!roots.length) return;

    const reduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    roots.forEach((root) => {
      if (root.dataset.xCcReady === "1") return; // 멱등
      root.dataset.xCcReady = "1";
      root.dataset.level = String(STILL_LEVEL);

      if (reduced) return; // 정지 — 기본 단계만 두고 끝낸다

      let timer = 0;
      const step = () => {
        const cur = Number(root.dataset.level) || STILL_LEVEL;
        root.dataset.level = String(cur >= MAX_LEVEL ? MIN_LEVEL : cur + 1);
        timer = window.setTimeout(step, HOLD);
      };

      if (!("IntersectionObserver" in window)) {
        // 관찰자가 없으면 그냥 돌린다 — 연출이 안 보이는 것보다 낫다.
        timer = window.setTimeout(step, HOLD);
        return;
      }

      const io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            if (!timer) timer = window.setTimeout(step, HOLD);
          } else {
            window.clearTimeout(timer);
            timer = 0;
          }
        },
        { threshold: 0.15 },
      );
      io.observe(root);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
}
